import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import CrisisResources from '../components/CrisisResources';
import ProblemComments from '../components/ProblemComments';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import { defaultProblemLabels, defaultProblemStatuses } from '../data/problems';
import {
  blockUser,
  createProblemContactRequest,
  createReport,
  deleteProblemDoc,
  listAcceptedProblemContactRequests,
  listBlockedUsers,
  listDiscoverableProblemDocs,
  listOutgoingProblemContactRequests,
  unblockUser,
} from '../services/contentService';
import { ensureUserProfile } from '../services/profileService';

function docLabels(doc) {
  return Array.isArray(doc.labels) && doc.labels.length ? doc.labels : [doc.topic || 'other'];
}

function docStatuses(doc) {
  return Array.isArray(doc.statuses) && doc.statuses.length ? doc.statuses : [doc.status || 'unknowns'];
}

function ProblemsSearch() {
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [docs, setDocs] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [outgoingContacts, setOutgoingContacts] = useState([]);
  const [acceptedContacts, setAcceptedContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [labelFilter, setLabelFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [contactMessages, setContactMessages] = useState({});
  const [reportReasons, setReportReasons] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadSearch = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setError('');
    try {
      const [loadedProfile, problemDocs, blocked, outgoing, accepted] = await Promise.all([
        ensureUserProfile(currentUser),
        listDiscoverableProblemDocs(currentUser.uid),
        listBlockedUsers(currentUser.uid),
        listOutgoingProblemContactRequests(currentUser.uid),
        listAcceptedProblemContactRequests(currentUser.uid),
      ]);
      setProfile(loadedProfile);
      setDocs(problemDocs);
      setBlockedUsers(blocked);
      setOutgoingContacts(outgoing);
      setAcceptedContacts(accepted);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadSearch();
  }, [loadSearch]);

  async function handleCreateContactRequest(doc) {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await createProblemContactRequest(profile || await ensureUserProfile(currentUser), doc, contactMessages[doc.id]);
      setContactMessages((current) => ({ ...current, [doc.id]: '' }));
      setMessage('Contact request sent.');
      await loadSearch();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleReport(doc) {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const reason = reportReasons[doc.id] || 'Harmful, unsafe, or inappropriate content';
      await createReport(currentUser.uid, 'problemDoc', doc.id, reason);
      setReportReasons((current) => ({ ...current, [doc.id]: '' }));
      setMessage('Report sent.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleBlock(ownerId) {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await blockUser(currentUser.uid, ownerId);
      setBlockedUsers((current) => [...new Set([...current, ownerId])]);
      setMessage('Contributor hidden from your Problems search.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleUnblock(ownerId) {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await unblockUser(currentUser.uid, ownerId);
      setBlockedUsers((current) => current.filter((uid) => uid !== ownerId));
      setMessage('Contributor unhidden.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(docId) {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await deleteProblemDoc(docId);
      setMessage('Problem deleted.');
      await loadSearch();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function contactRequestFor(doc) {
    return [...outgoingContacts, ...acceptedContacts].find((request) => (
      request.problemId === doc.id
      && request.fromUid === currentUser?.uid
      && request.toUid === doc.ownerId
    ));
  }

  function similarDocsFor(doc, visibleDocs) {
    const labels = docLabels(doc);
    return visibleDocs
      .filter((candidate) => candidate.id !== doc.id && candidate.visibility !== 'private' && docLabels(candidate).some((label) => labels.includes(label)))
      .slice(0, 3);
  }

  const blockedSet = new Set(blockedUsers);
  const visibleDocs = docs.filter((doc) => doc.ownerId === currentUser?.uid || !blockedSet.has(doc.ownerId));
  const labelOptions = [...new Set([...defaultProblemLabels, ...visibleDocs.flatMap(docLabels)])];
  const statusOptions = [...new Set([...defaultProblemStatuses, ...visibleDocs.flatMap(docStatuses)])];
  const normalizedSearch = search.trim().toLowerCase();
  const filteredDocs = visibleDocs.filter((doc) => (
    (labelFilter === 'all' || docLabels(doc).includes(labelFilter))
    && (statusFilter === 'all' || docStatuses(doc).includes(statusFilter))
    && (!normalizedSearch || (doc.searchableText || '').includes(normalizedSearch))
  ));

  return (
    <>
      <SEO title="Search Problems" />
      <section className="page-heading">
        <p className="eyebrow">Problem Search</p>
        <h1>Find related problems, labels, and people who opted in.</h1>
        <p>Search public problem documents and your own documents. Ask to connect only when a document feels relevant.</p>
      </section>
      {error && <p className="error-text">{error}</p>}
      {message && <p className="notice">{message}</p>}
      <CrisisResources />
      <section className="surface-card">
        <div className="section-toolbar">
          <div>
            <h2>Discover problem documents</h2>
            <p className="muted">Search text, labels, custom labels, and default/custom statuses.</p>
          </div>
          <Link className="button button-secondary" to="/dashboard/problems">Back to Problems</Link>
        </div>
        <div className="button-row compact-row problem-filters">
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search problems" />
          <select value={labelFilter} onChange={(event) => setLabelFilter(event.target.value)}>
            <option value="all">All labels</option>
            {labelOptions.map((label) => <option value={label} key={label}>{label}</option>)}
          </select>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="all">All statuses</option>
            {statusOptions.map((status) => <option value={status} key={status}>{status}</option>)}
          </select>
        </div>
        {blockedUsers.length ? (
          <div className="blocked-users-panel">
            <p className="tag">Hidden contributors</p>
            {blockedUsers.map((uid) => (
              <button className="button button-secondary button-small" type="button" disabled={saving} key={uid} onClick={() => handleUnblock(uid)}>Unhide {uid}</button>
            ))}
          </div>
        ) : null}
        {loading ? <p>Loading problem documents...</p> : null}
        {!loading && filteredDocs.length === 0 ? <p className="muted">No matching documents yet.</p> : null}
        <div className="item-list problem-doc-list">
          {filteredDocs.map((doc) => (
            <article className="content-item problem-doc" key={doc.id}>
              <p className="tag">{doc.visibility}</p>
              <h3>{doc.title}</h3>
              <p className="muted">By {doc.displayName}</p>
              <div className="chip-row compact-chip-row">
                {docLabels(doc).map((label) => <span className="static-chip" key={label}>{label}</span>)}
                {docStatuses(doc).map((status) => <span className="static-chip status-chip" key={status}>{status}</span>)}
              </div>
              <p>{doc.summary || doc.problem}</p>
              {doc.sourceTitle ? <p className="muted"><strong>Started from:</strong> {doc.sourceTitle}</p> : null}
              {doc.keyPoints ? <p><strong>Key points:</strong> {doc.keyPoints}</p> : null}
              {doc.solution ? <p><strong>What helped:</strong> {doc.solution}</p> : null}
              {currentUser?.uid === doc.ownerId ? (
                <div className="button-row compact-row">
                  <button className="button button-secondary" type="button" disabled={saving} onClick={() => handleDelete(doc.id)}>Delete</button>
                </div>
              ) : (
                <>
                  <div className="problem-contact-actions">
                    <label>Connection note
                      <input value={contactMessages[doc.id] || ''} onChange={(event) => setContactMessages((current) => ({ ...current, [doc.id]: event.target.value }))} placeholder="Why would you like to connect?" />
                    </label>
                    {contactRequestFor(doc) ? (
                      <p className="muted">Contact request is {contactRequestFor(doc).status}.</p>
                    ) : (
                      <button className="button button-secondary button-small" type="button" disabled={saving} onClick={() => handleCreateContactRequest(doc)}>Ask to connect</button>
                    )}
                  </div>
                  <div className="problem-safety-actions">
                    <select value={reportReasons[doc.id] || 'Harmful, unsafe, or inappropriate content'} onChange={(event) => setReportReasons((current) => ({ ...current, [doc.id]: event.target.value }))}>
                      <option>Harmful, unsafe, or inappropriate content</option>
                      <option>Harassment or abuse</option>
                      <option>Spam or misleading content</option>
                      <option>Privacy or personal information concern</option>
                    </select>
                    <button className="button button-secondary button-small" type="button" disabled={saving} onClick={() => handleReport(doc)}>Report</button>
                    <button className="button button-secondary button-small" type="button" disabled={saving} onClick={() => handleBlock(doc.ownerId)}>Hide contributor</button>
                  </div>
                </>
              )}
              {similarDocsFor(doc, visibleDocs).length ? (
                <div className="similar-problems">
                  <p className="tag">Similar documents</p>
                  {similarDocsFor(doc, visibleDocs).map((similar) => (
                    <div className="similar-problem" key={similar.id}>
                      <strong>{similar.title}</strong>
                      <span>{similar.summary || similar.problem}</span>
                    </div>
                  ))}
                </div>
              ) : null}
              <ProblemComments problem={doc} />
            </article>
          ))}
        </div>
      </section>
    </>
  );
}

export default ProblemsSearch;
